import { extractionMethodForMigrationSource } from "@common/helpers/documentMigration";
import {
    ExtractionStatus,
    GlExtractionBasis,
} from "@common/types/financialDocument";
import type { CoaExtractionRecord } from "@common/schemas/financialDocument/coa-extraction-record.schema";
import type { GlExtractionRecord } from "@common/schemas/financialDocument/gl-extraction-record.schema";
import type { CoADocumentMigrationSource } from "@common/schemas/accounting-integration.schema";
import { ImportRejection } from "@common/schemas/domains/accounting/import-rejection.schema";
import { GlBasis } from "@common/schemas/accounting-integration.schema";
import type {
    MigrationDoc,
    MigrationPeriod,
    MigrationStatusText,
} from "./migrationFileMatching.types";
import {
    formatCoaSummary,
    formatGlSummary,
} from "./migrationFileUploadFormatting";

function getExtractionRowCount<T>(extraction: {
    rowsCount?: number;
    rows?: readonly T[] | null;
}): number {
    return extraction.rowsCount ?? extraction.rows?.length ?? 0;
}

function glBasisToRole(basis: GlExtractionBasis): MigrationDoc["role"] {
    switch (basis) {
        case GlExtractionBasis.CASH:
            return "cashGl";
        case GlExtractionBasis.ACCRUAL:
            return "accrualGl";
        case GlExtractionBasis.UNKNOWN:
            return "unknownGl";
    }
}

function userChosenBasisFromGl(basis: GlExtractionBasis): GlBasis | undefined {
    switch (basis) {
        case GlExtractionBasis.CASH:
            return "cashGl";
        case GlExtractionBasis.ACCRUAL:
            return "accrualGl";
        case GlExtractionBasis.UNKNOWN:
            return undefined;
    }
}

export function rejectionStatusText(
    rejection: ImportRejection,
): MigrationStatusText {
    return { short: rejection.summary, tooltip: rejection.guidance };
}

export type DocAction =
    | { type: "ADD_EXTRACTING"; documentId: number; fileName: string }
    | {
          type: "EXTRACTION_SUCCESS";
          documentId: number;
          role: MigrationDoc["role"];
          period: MigrationPeriod | null;
          summary: string;
          warnings?: string[];
      }
    | {
          type: "MARK_UNVERIFIED";
          documentId: number;
          statusMessage: MigrationStatusText;
          rejection?: ImportRejection;
      }
    | { type: "SET_BASIS"; documentId: number; basis: GlBasis }
    | { type: "DISMISS_PARSER_WARNING"; documentId: number }
    | { type: "REMOVE"; documentId: number }
    | { type: "READD"; doc: MigrationDoc }
    | { type: "RESTORE"; docs: MigrationDoc[] };

export function docsReducer(
    state: MigrationDoc[],
    action: DocAction,
): MigrationDoc[] {
    switch (action.type) {
        case "ADD_EXTRACTING": {
            const withoutExisting = state.filter(
                (doc) => doc.documentId !== action.documentId,
            );
            return [
                ...withoutExisting,
                {
                    documentId: action.documentId,
                    fileName: action.fileName,
                    status: "extracting",
                },
            ];
        }
        case "EXTRACTION_SUCCESS":
            return state.map((doc) =>
                doc.documentId === action.documentId
                    ? {
                          ...doc,
                          status: "extracted",
                          role: action.role,
                          period: action.period,
                          summary: action.summary,
                          warnings: action.warnings,
                          statusMessage: undefined,
                      }
                    : doc,
            );
        case "SET_BASIS":
            return state.map((doc) =>
                doc.documentId === action.documentId
                    ? { ...doc, chosenBasis: action.basis }
                    : doc,
            );
        case "DISMISS_PARSER_WARNING":
            return state.map((doc) => {
                if (doc.documentId !== action.documentId) {
                    return doc;
                }
                const warningCount = doc.warnings?.length ?? 0;
                if (warningCount === 0) {
                    return doc;
                }
                return {
                    ...doc,
                    dismissedParserWarningCount: Math.min(
                        (doc.dismissedParserWarningCount ?? 0) + 1,
                        warningCount,
                    ),
                };
            });
        case "MARK_UNVERIFIED":
            return state.map((doc) =>
                doc.documentId === action.documentId
                    ? {
                          ...doc,
                          status: "unrecognized",
                          statusMessage: action.statusMessage,
                          rejection: action.rejection,
                      }
                    : doc,
            );
        case "REMOVE":
            return state.filter((doc) => doc.documentId !== action.documentId);
        case "READD": {
            const withoutExisting = state.filter(
                (doc) => doc.documentId !== action.doc.documentId,
            );
            return [...withoutExisting, action.doc];
        }
        case "RESTORE":
            return action.docs;
    }
}

export function migrationDocsToHydrate(
    currentDocs: readonly MigrationDoc[],
    restoredDocs: readonly MigrationDoc[],
): MigrationDoc[] {
    const existingIds = new Set(currentDocs.map((doc) => doc.documentId));
    return restoredDocs.filter((doc) => !existingIds.has(doc.documentId));
}

export function normalizedExtractionWarnings(
    warnings: string[] | null | undefined,
): string[] | undefined {
    if (warnings == null || warnings.length === 0) {
        return undefined;
    }
    return warnings;
}

/**
 * The minimal document shape the migration grid needs; satisfied by both the
 * task-detail `financialDocuments` records and the extended document DTO.
 */
export interface MigrationRestoreDocument {
    id: number;
    fileName: string;
    coaExtraction?: CoaExtractionRecord | null;
    glExtraction?: GlExtractionRecord | null;
}

export function getDocRestoration(
    document: MigrationRestoreDocument,
    source: CoADocumentMigrationSource,
): MigrationDoc {
    const extractionMethod = extractionMethodForMigrationSource(source);

    const coa = document.coaExtraction;
    if (
        coa &&
        coa.status === ExtractionStatus.COMPLETED &&
        coa.extractionMethod === extractionMethod
    ) {
        return {
            documentId: document.id,
            fileName: document.fileName,
            status: "extracted",
            role: "coa",
            period: null,
            summary: formatCoaSummary(getExtractionRowCount(coa)),
            warnings: normalizedExtractionWarnings(coa.warnings),
        };
    }
    if (
        coa &&
        coa.status === ExtractionStatus.REJECTED &&
        coa.rejection != null
    ) {
        return {
            documentId: document.id,
            fileName: document.fileName,
            status: "unrecognized",
            statusMessage: rejectionStatusText(coa.rejection),
            rejection: coa.rejection,
        };
    }

    const gl = document.glExtraction;
    if (gl && gl.status === ExtractionStatus.REJECTED && gl.rejection != null) {
        return {
            documentId: document.id,
            fileName: document.fileName,
            status: "unrecognized",
            statusMessage: rejectionStatusText(gl.rejection),
            rejection: gl.rejection,
        };
    }
    if (
        gl &&
        gl.status === ExtractionStatus.COMPLETED &&
        gl.extractionMethod === extractionMethod &&
        gl.basis
    ) {
        const period = gl.startDate
            ? { start: gl.startDate, end: gl.endDate ?? null }
            : null;
        const summary = formatGlSummary(getExtractionRowCount(gl));
        const chosenBasis = userChosenBasisFromGl(gl.basis);
        const warnings = normalizedExtractionWarnings(gl.warnings);
        if (gl.isChangedByUser && chosenBasis != null) {
            return {
                documentId: document.id,
                fileName: document.fileName,
                status: "extracted",
                role: "unknownGl",
                chosenBasis,
                period,
                summary,
                warnings,
            };
        }
        return {
            documentId: document.id,
            fileName: document.fileName,
            status: "extracted",
            role: glBasisToRole(gl.basis),
            period,
            summary,
            warnings,
        };
    }

    // Linked docs without a matching extraction are parsed when the task opens.
    return {
        documentId: document.id,
        fileName: document.fileName,
        status: "extracting",
    };
}
